import { type EmojiProvider } from '@atlaskit/emoji/resource';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import { Reactions as AkReactions } from '@atlaskit/reactions';
import { N90 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { type ReactionsSummaryQueryResponse } from '@post-office/growth-common';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { useMessageEvent } from '@post-office/message-lifecycle';
import { type ComponentProps, Suspense, useCallback } from 'react';
import { FormattedMessage } from 'react-intl-next';

import messages from '../../messages/messages';
import { type ExperienceTrackingAttributes, ReactionStatus } from '../../types/reaction-types';
import { reactionsMapper } from '../../utils';
import { ReactionsPlaceholder } from '../reactions-placeholder';
import { type JSX } from 'react/jsx-runtime';

const noop = () => {};

export type ReactionsProps = {
	cloudId: string;
	contentId: string;
	containerId: string;
	containerType: string;
	contentType: 'COMMENT' | 'PAGE' | 'BLOGPOST';
	emojiProvider: Promise<EmojiProvider>;
	experienceTrackingAttributes: ExperienceTrackingAttributes;
	reactionsList: ReactionsSummaryQueryResponse | undefined;
	deleteReaction?: (config: unknown) => void;
	addReaction?: (config: unknown) => void;
	getReactionsData?: (config: unknown) => void;
	onMutationCompleted?: (data: unknown) => void;
	onMutationError?: (error: Error) => void;
	summaryViewEnabled?: boolean;
	quickReactionEmojis?: ComponentProps<typeof AkReactions>['quickReactionEmojis'];
	showAddReactionText?: boolean;
	particleEffectByEmoji?: ComponentProps<typeof AkReactions>['particleEffectByEmoji'];
	fireAnalyticsEvents?: boolean;
};

export const ReactionWrapper = ({
	cloudId,
	containerId,
	containerType = 'page',
	contentId,
	reactionsList,
	emojiProvider,
	addReaction,
	deleteReaction,
	onMutationCompleted,
	onMutationError,
	experienceTrackingAttributes,
	summaryViewEnabled = false,
	contentType,
	quickReactionEmojis,
	showAddReactionText,
	particleEffectByEmoji,
	fireAnalyticsEvents = true,
}: ReactionsProps): JSX.Element => {
	const reactionsSummary = reactionsMapper(reactionsList);
	const { messageInstanceClicked } = useMessageInstanceEvent();
	const { messageClicked } = useMessageEvent();

	const reactionsSummaryError = false; // track api failure for fetching reactions summary

	const handleReaction = useCallback(
		(emojiId: string, isAdd: boolean) => {
			if (addReaction && deleteReaction) {
				// do mutation to add or delete reaction
				const commitMutation = isAdd ? addReaction : deleteReaction;
				// call the callback from coming from message creator
				commitMutation({
					variables: {
						cloudId,
						containerId,
						containerType: containerType.toUpperCase(),
						contentId,
						contentType,
						emojiId,
					},
					onCompleted: onMutationCompleted,
					onError: onMutationError,
				});

				// Adding an override for this call because some templates might not want these events
				if (fireAnalyticsEvents) {
					messageClicked().catch(() => {});

					// Send Experience tracking event
					messageInstanceClicked({
						messageEventType: 'clicked',
						clickedElement: {
							clickIdentifier: 'reactionEmoji',
							clickedElement: 'button',
							buttonType: 'primary',
						},
						action: {
							actionType: 'acknowledge',
						},
						additionalMetadataFromFrontend: {
							...experienceTrackingAttributes,
							isReactionAdded: isAdd.toString(),
							isReactionRemoved: (!isAdd).toString(),
						},
					}).catch(() => {});
				}
			}
		},
		[
			addReaction,
			deleteReaction,
			contentId,
			contentType,
			containerId,
			containerType,
			cloudId,
			onMutationCompleted,
			onMutationError,
		],
	);

	const onReactionClick = useCallback(
		(emojiId: string) => {
			const existingReaction = reactionsSummary.find((reaction) => reaction.emojiId === emojiId);
			handleReaction(emojiId, !existingReaction?.reacted);
		},
		[reactionsSummary, handleReaction],
	);

	const onSelection = useCallback(
		(emojiId: string) => {
			const existingReaction = reactionsSummary.find((reaction) => reaction.emojiId === emojiId);
			if (!existingReaction?.reacted) {
				handleReaction(emojiId, true);
			}
		},
		[reactionsSummary, handleReaction],
	);

	return (
		<Suspense fallback={<ReactionsPlaceholder />}>
			{reactionsSummaryError ? (
				<Inline space="space.100" alignBlock="center">
					<ErrorIcon
						color={token('color.icon.accent.gray', N90)}
						label="Icon showing an error with rendering reactions"
						size="small"
					/>
					<Text color={'color.text.subtlest'}>
						<FormattedMessage {...messages.viewReactionsError} />
					</Text>
				</Inline>
			) : (
				<AkReactions
					reactions={reactionsSummary}
					loadReaction={noop}
					onSelection={onSelection}
					onReactionClick={onReactionClick}
					status={ReactionStatus.ready}
					emojiProvider={emojiProvider}
					summaryViewEnabled={summaryViewEnabled}
					allowAllEmojis={true}
					emojiPickerSize="small"
					miniMode
					noRelativeContainer
					showAddReactionText={showAddReactionText}
					quickReactionEmojis={quickReactionEmojis}
					particleEffectByEmoji={particleEffectByEmoji}
				/>
			)}
		</Suspense>
	);
};
